import supabase from "@/services/supabaseClient";

const VALID_STATUS = new Set(["good", "acceptable", "caution", "critical"]);

function normalizeStatus(value) {
  const status = String(value || "").trim().toLowerCase();
  if (VALID_STATUS.has(status)) return status;
  return "acceptable";
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toUpdateLabel(value) {
  if (!value) return "Today";
  try {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return "Today";
    const now = new Date();
    const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startThen = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startNow.getTime() - startThen.getTime()) / 86400000);
    if (diffDays <= 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return d.toLocaleDateString();
  } catch {
    return "Today";
  }
}

function mapSignalRow(row = {}) {
  const lat = toNumber(row.latitude ?? row.lat);
  const lng = toNumber(row.longitude ?? row.lng ?? row.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return {
    id: String(row.signal_key || row.id || `${lat},${lng}`),
    country: String(row.country || "").trim(),
    province: String(row.province || "").trim(),
    district: String(row.district || "").trim(),
    region: String(row.region || row.province || row.district || "").trim(),
    disease: String(row.disease || row.disease_name || "Outbreak alert").trim(),
    status: normalizeStatus(row.status || row.severity),
    patients: Math.max(0, Number(row.patients ?? row.patient_count ?? 0) || 0),
    updated: toUpdateLabel(row.observed_at || row.updated_at || row.created_at),
    observedAt: row.observed_at || row.updated_at || row.created_at || null,
    source: String(row.source || "db").trim() || "db",
    lat,
    lng,
  };
}

const BLOCKING_SEVERITIES = new Set(["error"]);
const DEMO_SCENARIO = Object.freeze({
  programCode: "EBOLA_DEMO",
  orgUnitCode: "DEMO_FACILITY_A",
  reportingPeriod: "2026W28",
  periodType: "weekly",
  valuesByCode: Object.freeze({
    suspected_cases: 12,
    probable_cases: 0,
    confirmed_cases: 4,
    deaths: 1,
    recoveries: 1,
    contacts_listed: 38,
    contacts_followed_today: 31,
    contacts_missed_today: 7,
    lab_samples_collected: 8,
    lab_samples_pending: 3,
    lab_samples_positive: 4,
    healthcare_worker_infections: 1,
  }),
});

function safeText(value) {
  return String(value || "").trim();
}

function numberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function byCode(items = []) {
  return new Map((Array.isArray(items) ? items : []).map((item) => [item.code, item]));
}

function valueMap(values = []) {
  const map = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const code = value?.indicator?.code || value?.indicator_code || value?.code;
    if (code) map.set(code, numberOrNull(value.value_numeric ?? value.value));
  });
  return map;
}

function buildFlag({
  reportId,
  reportVersion = 1,
  severity = "error",
  issueType = "validation_error",
  code,
  message,
  indicatorId = null,
  provisional = false,
  metadata = {},
}) {
  return {
    report_id: reportId,
    report_version: reportVersion,
    severity,
    issue_type: issueType,
    code,
    message,
    indicator_id: indicatorId,
    resolved: false,
    provisional,
    metadata,
  };
}

function mappingKey(mappingType, localEntityType, localId, localCode = "") {
  return [
    safeText(mappingType),
    safeText(localEntityType),
    safeText(localId),
    safeText(localCode),
  ].join(":");
}

function mapMappings(items = []) {
  const map = new Map();
  (Array.isArray(items) ? items : []).forEach((mapping) => {
    map.set(
      mappingKey(
        mapping.mapping_type,
        mapping.local_entity_type,
        mapping.local_entity_id,
        mapping.local_code
      ),
      mapping
    );
  });
  return map;
}

function isMapped(mapping) {
  return safeText(mapping?.status) === "mapped" && safeText(mapping?.dhis2_uid);
}

function previewComment(report) {
  return [
    "AfriMed™ Regional Health demo export preview only",
    `Report ${report?.id || "unknown"}`,
  ].join(" - ");
}

function isReportVersion(row, version) {
  return Number(row?.report_version || 0) === Number(version || 0);
}

export const regionalHealthService = {
  async createInstitutionReportingFacility({ institutionId, facilityName, facilityType, country, province, districtOrHealthZone, latitude, longitude } = {}) {
    const { data, error } = await supabase.rpc("regional_health_institution_create_reporting_facility", {
      target_institution: safeText(institutionId),
      facility_name_input: safeText(facilityName),
      facility_type_input: safeText(facilityType),
      country_input: safeText(country),
      province_input: safeText(province),
      district_input: safeText(districtOrHealthZone),
      latitude_input: Number(latitude),
      longitude_input: Number(longitude),
    });
    return { data: error ? null : data, error: error || null };
  },

  async saveInstitutionReportingProfile({ institutionId, facilityId, reportingLanguage, aggregateReportingEnabled, reportingCadence, programmeId = null, programmeRequired = false, reportingMemberId = null, dhis2MappingVersion = "", dhis2OrgUnitMappingStatus = "unmapped", dhis2DataElementMappingStatus = "unmapped", dhis2CategoryOptionMappingStatus = "unmapped" } = {}) {
    const { data, error } = await supabase.rpc("regional_health_institution_save_reporting_profile", {
      target_institution: safeText(institutionId),
      target_facility: facilityId || null,
      reporting_language_input: safeText(reportingLanguage) || "en",
      aggregate_reporting_enabled_input: Boolean(aggregateReportingEnabled),
      reporting_cadence_input: safeText(reportingCadence) || null,
      programme_input: programmeId || null,
      programme_required_input: Boolean(programmeRequired),
      reporting_member_input: reportingMemberId || null,
      dhis2_mapping_version_input: safeText(dhis2MappingVersion) || null,
      dhis2_org_unit_mapping_status_input: safeText(dhis2OrgUnitMappingStatus) || "unmapped",
      dhis2_data_element_mapping_status_input: safeText(dhis2DataElementMappingStatus) || "unmapped",
      dhis2_category_option_mapping_status_input: safeText(dhis2CategoryOptionMappingStatus) || "unmapped",
    });
    return { data: error ? null : data, error: error || null };
  },

  async loadInstitutionSignalRegistry({ institutionId } = {}) {
    const targetInstitution = safeText(institutionId);
    if (!targetInstitution) {
      return { data: null, error: new Error("An active Institution context is required.") };
    }
    const { data, error } = await supabase.rpc("regional_health_institution_signal_snapshot", {
      target_institution: targetInstitution,
    });
    return { data: error ? null : (data || {}), error: error || null };
  },

  async loadInstitutionHeatmapSnapshot({
    institutionId,
    facilityId = null,
    programmeId = null,
    reportingPeriodStart = null,
    reportingPeriodEnd = null,
    workflowState = null,
    dataQualityState = null,
  } = {}) {
    const targetInstitution = safeText(institutionId);
    if (!targetInstitution) {
      return { data: null, error: new Error("An active Institution context is required.") };
    }
    const { data, error } = await supabase.rpc("regional_health_institution_heatmap_snapshot", {
      target_institution: targetInstitution,
      facility_filter: safeText(facilityId) || null,
      programme_filter: safeText(programmeId) || null,
      reporting_period_start: reportingPeriodStart || null,
      reporting_period_end: reportingPeriodEnd || null,
      workflow_filter: safeText(workflowState) || null,
      quality_filter: safeText(dataQualityState) || null,
    });
    return { data: error ? null : (data || {}), error: error || null };
  },

  async saveInstitutionSignalDraft({ institutionId, signalId = null, facilityId, programmeId = null, eventCategory, classification, eventDate, reportDate, affectedCount, deathsCount, narrative } = {}) {
    const { data, error } = await supabase.rpc("regional_health_institution_save_signal_draft", {
      target_institution: safeText(institutionId),
      target_signal: signalId || null,
      target_facility: facilityId || null,
      target_programme: programmeId || null,
      event_category_input: safeText(eventCategory),
      classification_input: safeText(classification) || "other_unknown",
      event_date_input: eventDate || null,
      report_date_input: reportDate || null,
      affected_count_input: Number(affectedCount || 0),
      deaths_count_input: Number(deathsCount || 0),
      narrative_input: safeText(narrative),
    });
    return { data: error ? null : data, error: error || null };
  },

  async transitionInstitutionSignal({ institutionId, signalId, transition, reason = "" } = {}) {
    const { data, error } = await supabase.rpc("regional_health_institution_transition_signal", {
      target_institution: safeText(institutionId),
      target_signal: signalId,
      transition_input: safeText(transition),
      reason_input: safeText(reason) || null,
    });
    return { data: error ? null : data, error: error || null };
  },

  async loadInstitutionEvaluationSnapshot({ institutionId } = {}) {
    const targetInstitution = safeText(institutionId);
    if (!targetInstitution) {
      return { data: null, error: new Error("An active Institution context is required.") };
    }

    const { data, error } = await supabase.rpc("regional_health_institution_surveillance_snapshot", {
      target_institution: targetInstitution,
    });
    if (error) return { data: null, error };

    const snapshot = data && typeof data === "object" ? data : {};
    const orgUnits = (Array.isArray(snapshot.facilities) ? snapshot.facilities : [])
      .filter((facility) => safeText(facility?.regionalHealthOrgUnitId))
      .map((facility) => ({
        id: facility.regionalHealthOrgUnitId,
        name: facility.orgUnitName || facility.facilityName || "Institution facility",
        code: facility.orgUnitCode || "",
        org_unit_type: facility.orgUnitType || "facility",
      }));

    return {
      data: {
        programs: Array.isArray(snapshot.programmes) ? snapshot.programmes : [],
        orgUnits,
        indicators: [],
        mappings: [],
        reports: [],
        assignments: Array.isArray(snapshot.assignments) ? snapshot.assignments : [],
        authorityEvents: Array.isArray(snapshot.events) ? snapshot.events : [],
        scope: {
          institutionId: targetInstitution,
          facilityCount: orgUnits.length,
          programmeCount: Array.isArray(snapshot.programmes) ? snapshot.programmes.length : 0,
          assignmentCount: Array.isArray(snapshot.assignments) ? snapshot.assignments.length : 0,
        },
      },
      error: null,
    };
  },

  async getSignals({ country = "", limit = 500 } = {}) {
    try {
      let query = supabase
        .from("regional_health_signals")
        .select(
          "id,signal_key,country,province,district,region,disease,status,patients,latitude,longitude,source,observed_at,updated_at,created_at"
        )
        .order("observed_at", { ascending: false, nullsFirst: false })
        .order("updated_at", { ascending: false, nullsFirst: false })
        .limit(Math.max(1, Math.min(1000, Number(limit) || 500)));

      const normalizedCountry = String(country || "").trim();
      if (normalizedCountry) {
        query = query.eq("country", normalizedCountry);
      }

      const { data, error } = await query;
      if (error) return { data: [], error };

      const mapped = (Array.isArray(data) ? data : [])
        .map((row) => mapSignalRow(row))
        .filter(Boolean);

      return { data: mapped, error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async listSurveillancePrograms() {
    const { data, error } = await supabase
      .from("regional_health_surveillance_programs")
      .select("*")
      .order("name", { ascending: true });
    return { data: data || [], error };
  },

  async listOrgUnits() {
    const { data, error } = await supabase
      .from("regional_health_org_units")
      .select("*")
      .order("org_unit_type", { ascending: true })
      .order("name", { ascending: true });
    return { data: data || [], error };
  },

  async listIndicators() {
    const { data, error } = await supabase
      .from("regional_health_indicators")
      .select("*")
      .order("category", { ascending: true })
      .order("name", { ascending: true });
    return { data: data || [], error };
  },

  async listMappings() {
    const { data, error } = await supabase
      .from("regional_health_dhis2_mappings")
      .select("*")
      .order("mapping_type", { ascending: true })
      .order("local_label", { ascending: true });
    return { data: data || [], error };
  },

  async upsertMapping(mapping = {}) {
    const status = safeText(mapping.status) || "unmapped";
    const uid = safeText(mapping.dhis2_uid);
    if (status === "mapped" && !uid) {
      return {
        data: null,
        error: new Error("A DHIS2 UID is required before marking a mapping as mapped."),
      };
    }

    const payload = {
      mapping_type: safeText(mapping.mapping_type),
      local_entity_type: safeText(mapping.local_entity_type),
      local_entity_id: mapping.local_entity_id || null,
      local_code: safeText(mapping.local_code),
      local_label: safeText(mapping.local_label),
      dhis2_uid: uid || null,
      dhis2_name: safeText(mapping.dhis2_name) || null,
      dhis2_instance_label: safeText(mapping.dhis2_instance_label) || "Demo DHIS2 sandbox",
      status,
      notes: safeText(mapping.notes) || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("regional_health_dhis2_mappings")
      .upsert(payload, {
        onConflict: "mapping_type,local_entity_type,local_entity_id,local_code",
      })
      .select()
      .single();
    return { data, error };
  },

  async createAggregateReport({ programId, orgUnitId, reportingPeriod, periodType = "weekly" } = {}) {
    const { data: userData } = await supabase.auth.getUser();
    const payload = {
      program_id: programId,
      org_unit_id: orgUnitId,
      reporting_period: safeText(reportingPeriod),
      period_type: safeText(periodType) || "weekly",
      status: "draft",
      quality_status: "not_assessed",
      created_by: userData?.user?.id || null,
      source_classification: "admin_demo",
      source_reference: "regional_health_facility_intake_preview",
      current_responsible_role: "facility_surveillance_officer",
    };
    const { data, error } = await supabase
      .from("regional_health_aggregate_reports")
      .insert(payload)
      .select("*")
      .single();
    return { data, error };
  },

  async loadDemoScenario() {
    const [programsRes, orgUnitsRes, indicatorsRes] = await Promise.all([
      this.listSurveillancePrograms(),
      this.listOrgUnits(),
      this.listIndicators(),
    ]);
    const setupError = programsRes.error || orgUnitsRes.error || indicatorsRes.error;
    if (setupError) return { data: null, error: setupError };

    const program = (programsRes.data || []).find((item) => item.code === DEMO_SCENARIO.programCode);
    const orgUnit = (orgUnitsRes.data || []).find((item) => item.code === DEMO_SCENARIO.orgUnitCode);
    if (!program || !orgUnit) {
      return {
        data: null,
        error: new Error("Demo surveillance programme or org unit is not available in this environment."),
      };
    }

    const existingRes = await supabase
      .from("regional_health_aggregate_reports")
      .select("id")
      .eq("program_id", program.id)
      .eq("org_unit_id", orgUnit.id)
      .eq("reporting_period", DEMO_SCENARIO.reportingPeriod)
      .eq("period_type", DEMO_SCENARIO.periodType)
      .order("created_at", { ascending: false })
      .limit(1);
    if (existingRes.error) return { data: null, error: existingRes.error };

    let reportId = existingRes.data?.[0]?.id || "";
    if (!reportId) {
      const created = await this.createAggregateReport({
        programId: program.id,
        orgUnitId: orgUnit.id,
        reportingPeriod: DEMO_SCENARIO.reportingPeriod,
        periodType: DEMO_SCENARIO.periodType,
      });
      if (created.error) return created;
      reportId = created.data?.id || "";
    }

    const indicatorByCode = byCode(indicatorsRes.data || []);
    const valuesByIndicator = {};
    Object.entries(DEMO_SCENARIO.valuesByCode).forEach(([code, value]) => {
      const indicator = indicatorByCode.get(code);
      if (indicator?.id) valuesByIndicator[indicator.id] = value;
    });

    const valuesRes = await this.saveAggregateValues(reportId, valuesByIndicator);
    if (valuesRes.error) return { data: null, error: valuesRes.error };

    const qualityRes = await this.runDataQualityChecks(reportId);
    if (qualityRes.error) return { data: null, error: qualityRes.error };

    return this.getAggregateReportDetail(reportId);
  },

  async getAggregateReports() {
    const { data, error } = await supabase
      .from("regional_health_aggregate_reports")
      .select(
        "*, program:regional_health_surveillance_programs(*), org_unit:regional_health_org_units(*)"
      )
      .order("created_at", { ascending: false })
      .limit(100);
    return { data: data || [], error };
  },

  async getAggregateReportDetail(reportId) {
    const id = safeText(reportId);
    if (!id) return { data: null, error: new Error("Report id is required.") };

    const [reportRes, valuesRes, flagsRes, previewsRes, versionsRes, eventsRes, provenanceRes] = await Promise.all([
      supabase
        .from("regional_health_aggregate_reports")
        .select("*, program:regional_health_surveillance_programs(*), org_unit:regional_health_org_units(*)")
        .eq("id", id)
        .single(),
      supabase
        .from("regional_health_aggregate_values")
        .select("*, indicator:regional_health_indicators(*)")
        .eq("report_id", id),
      supabase
        .from("regional_health_data_quality_flags")
        .select("*, indicator:regional_health_indicators(*)")
        .eq("report_id", id)
        .order("created_at", { ascending: false }),
      supabase
        .from("regional_health_export_previews")
        .select("*")
        .eq("report_id", id)
        .order("created_at", { ascending: false }),
      supabase
        .from("regional_health_report_versions")
        .select("*")
        .eq("report_id", id)
        .order("version_number", { ascending: false }),
      supabase
        .from("regional_health_report_events")
        .select("*, org_unit:regional_health_org_units(name,code,org_unit_type)")
        .eq("report_id", id)
        .order("created_at", { ascending: true }),
      supabase
        .from("regional_health_value_provenance")
        .select("*, indicator:regional_health_indicators(code,name)")
        .eq("report_id", id)
        .order("report_version", { ascending: false }),
    ]);

    const error = reportRes.error || valuesRes.error || flagsRes.error || previewsRes.error ||
      versionsRes.error || eventsRes.error || provenanceRes.error;
    if (error) return { data: null, error };

    const currentVersion = Number(reportRes.data?.current_version || 1);
    const approvedVersion = Number(reportRes.data?.approved_version || 0);
    const allFlags = flagsRes.data || [];
    const currentFlags = allFlags.filter((flag) => isReportVersion(flag, currentVersion));
    const historicalFlags = allFlags.filter((flag) => !isReportVersion(flag, currentVersion));
    const allPreviews = previewsRes.data || [];
    const approvedPreviews = approvedVersion
      ? allPreviews.filter((preview) => isReportVersion(preview, approvedVersion))
      : allPreviews;

    return {
      data: {
        ...(reportRes.data || {}),
        values: valuesRes.data || [],
        flags: currentFlags,
        historical_flags: historicalFlags,
        all_flags: allFlags,
        export_previews: approvedPreviews,
        all_export_previews: allPreviews,
        versions: versionsRes.data || [],
        events: eventsRes.data || [],
        provenance: provenanceRes.data || [],
      },
      error: null,
    };
  },

  async saveAggregateValues(reportId, valuesByIndicator = {}) {
    const id = safeText(reportId);
    if (!id) return { data: null, error: new Error("Report id is required.") };

    const rows = Object.entries(valuesByIndicator)
      .map(([indicatorId, value]) => ({
        report_id: id,
        indicator_id: indicatorId,
        value_numeric: numberOrNull(value),
        value_text: value === "" || value === null || value === undefined ? null : String(value),
        updated_at: new Date().toISOString(),
      }))
      .filter((row) => row.indicator_id);

    const deleteRes = await supabase
      .from("regional_health_aggregate_values")
      .delete()
      .eq("report_id", id);
    if (deleteRes.error) return { data: null, error: deleteRes.error };

    if (!rows.length) return { data: [], error: null };

    const { data, error } = await supabase
      .from("regional_health_aggregate_values")
      .insert(rows)
      .select("*, indicator:regional_health_indicators(*)");
    return { data: data || [], error };
  },

  async runDataQualityChecks(reportId) {
    const detailRes = await this.getAggregateReportDetail(reportId);
    if (detailRes.error) return detailRes;

    const [indicatorsRes, mappingsRes] = await Promise.all([
      this.listIndicators(),
      this.listMappings(),
    ]);
    if (indicatorsRes.error) return { data: null, error: indicatorsRes.error };
    if (mappingsRes.error) return { data: null, error: mappingsRes.error };

    const report = detailRes.data;
    const reportVersion = Number(report.current_version || 1);
    const makeFlag = (input) => buildFlag({ reportVersion, ...input });
    const indicators = indicatorsRes.data || [];
    const indicatorByCode = byCode(indicators);
    const values = valueMap(report.values || []);
    const mappings = mapMappings(mappingsRes.data || []);
    const flags = [];

    indicators
      .filter((indicator) => indicator.required)
      .forEach((indicator) => {
        const value = values.get(indicator.code);
        if (value === null || value === undefined) {
          flags.push(makeFlag({
            reportId,
            severity: "error",
            code: "required_indicator_missing",
            message: `${indicator.name} is required before export preview.`,
            indicatorId: indicator.id,
          }));
        }
      });

    (report.values || []).forEach((row) => {
      const value = numberOrNull(row.value_numeric);
      if (value !== null && value < 0) {
        flags.push(makeFlag({
          reportId,
          severity: "error",
          code: "negative_value",
          message: `${row.indicator?.name || "Indicator"} cannot be negative.`,
          indicatorId: row.indicator_id,
        }));
      }
    });

    const confirmed = values.get("confirmed_cases") || 0;
    const deaths = values.get("deaths") || 0;
    const labCollected = values.get("lab_samples_collected") || 0;
    const labPositive = values.get("lab_samples_positive") || 0;
    const contactsListed = values.get("contacts_listed") || 0;
    const contactsFollowed = values.get("contacts_followed_today") || 0;
    const contactsMissed = values.get("contacts_missed_today") || 0;

    if (deaths > confirmed) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "deaths_gt_confirmed",
        message: "Deaths cannot be greater than confirmed cases in this aggregate report.",
        indicatorId: indicatorByCode.get("deaths")?.id || null,
      }));
    }

    if (labPositive > labCollected) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "lab_positive_gt_collected",
        message: "Positive lab samples cannot be greater than samples collected.",
        indicatorId: indicatorByCode.get("lab_samples_positive")?.id || null,
      }));
    }

    if (contactsFollowed > contactsListed) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "contacts_followed_gt_listed",
        message: "Contacts followed today cannot be greater than contacts listed.",
        indicatorId: indicatorByCode.get("contacts_followed_today")?.id || null,
      }));
    }

    if (contactsMissed > contactsListed) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "contacts_missed_gt_listed",
        message: "Contacts missed today cannot be greater than contacts listed.",
        indicatorId: indicatorByCode.get("contacts_missed_today")?.id || null,
      }));
    }

    if (confirmed > 0 && labPositive <= 0) {
      flags.push(makeFlag({
        reportId,
        severity: "warning",
        code: "confirmed_without_lab_positive",
        message: "Confirmed cases exist, but lab positive count is zero or missing.",
        indicatorId: indicatorByCode.get("confirmed_cases")?.id || null,
      }));
    }

    if (!safeText(report.reporting_period)) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "reporting_period_missing",
        message: "Reporting period is required.",
      }));
    }

    const orgMapping = mappings.get(mappingKey("org_unit", "org_unit", report.org_unit_id, report.org_unit?.code));
    if (!isMapped(orgMapping)) {
      flags.push(makeFlag({
        reportId,
        severity: "error",
        code: "org_unit_unmapped",
        message: "Reporting org unit must be mapped to a DHIS2 orgUnit UID before preview.",
      }));
    }

    indicators.forEach((indicator) => {
      const mapping = mappings.get(mappingKey("data_element", "indicator", indicator.id, indicator.code));
      if (!isMapped(mapping)) {
        flags.push(makeFlag({
          reportId,
          severity: "warning",
          code: "indicator_unmapped",
          message: `${indicator.name} is not mapped to a DHIS2 dataElement UID.`,
          indicatorId: indicator.id,
        }));
      }
    });

    if (contactsListed > 0 && contactsFollowed / contactsListed < 0.95) {
      flags.push(makeFlag({
        reportId,
        severity: "warning",
        issueType: "reconciliation_discrepancy",
        code: "contact_follow_up_below_target",
        message: "Contact follow-up is below the 95% programme target and requires supervisor review.",
        indicatorId: indicatorByCode.get("contacts_followed_today")?.id || null,
        metadata: {
          target_percentage: 95,
          observed_percentage: Math.round((contactsFollowed / contactsListed) * 1000) / 10,
        },
      }));
    }

    const deleteRes = await supabase
      .from("regional_health_data_quality_flags")
      .delete()
      .eq("report_id", reportId)
      .eq("report_version", reportVersion);
    if (deleteRes.error) return { data: null, error: deleteRes.error };

    let insertedFlags = [];
    if (flags.length) {
      const insertRes = await supabase
        .from("regional_health_data_quality_flags")
        .insert(flags)
        .select("*, indicator:regional_health_indicators(*)");
      if (insertRes.error) return { data: null, error: insertRes.error };
      insertedFlags = insertRes.data || [];
    }

    const hasBlocking = flags.some((flag) => BLOCKING_SEVERITIES.has(flag.severity));
    const hasWarnings = flags.some((flag) => flag.severity === "warning");
    const qualityStatus = hasBlocking ? "failed" : hasWarnings ? "warning" : "passed";
    const qualitySummary = {
      status: qualityStatus,
      errors: flags.filter((flag) => flag.severity === "error").length,
      warnings: flags.filter((flag) => flag.severity === "warning").length,
      reconciliationDiscrepancies: flags.filter(
        (flag) => flag.issue_type === "reconciliation_discrepancy"
      ).length,
    };
    const qualityRpc = await supabase.rpc("regional_health_record_quality_check", {
      target_report: reportId,
      target_quality_status: qualityStatus,
      target_result: qualitySummary,
      target_source_channel: "regional_health_surveillance",
    });
    if (qualityRpc.error) return { data: null, error: qualityRpc.error };

    return {
      data: {
        flags: insertedFlags,
        status: qualityStatus,
      },
      error: null,
    };
  },

  async generateDhis2DataValueSetPreview(reportId) {
    const detailRes = await this.getAggregateReportDetail(reportId);
    if (detailRes.error) return detailRes;

    const [indicatorsRes, mappingsRes] = await Promise.all([
      this.listIndicators(),
      this.listMappings(),
    ]);
    if (indicatorsRes.error) return { data: null, error: indicatorsRes.error };
    if (mappingsRes.error) return { data: null, error: mappingsRes.error };

    const report = detailRes.data;
    const approvedVersion = Number(report.approved_version || 0);
    if (report.status !== "approved_for_export" && report.status !== "export_previewed") {
      return {
        data: null,
        error: new Error("National approval is required before generating an export preview."),
      };
    }
    const approvedSnapshot = (report.versions || []).find(
      (version) => Number(version.version_number) === approvedVersion
    );
    if (!approvedSnapshot || !approvedVersion) {
      return {
        data: null,
        error: new Error("The approved report version is not available."),
      };
    }
    const mappings = mapMappings(mappingsRes.data || []);
    const dataSetMapping = mappings.get(mappingKey("data_set", "program", report.program_id, report.program?.code));
    const orgMapping = mappings.get(mappingKey("org_unit", "org_unit", report.org_unit_id, report.org_unit?.code));
    const unmapped = [];

    if (!isMapped(dataSetMapping)) unmapped.push("dataSet");
    if (!isMapped(orgMapping)) unmapped.push("orgUnit");

    const dataValues = Object.entries(approvedSnapshot.values_snapshot || {})
      .map(([code, snapshotValue]) => {
        const indicator = (indicatorsRes.data || []).find((item) => item.code === code) || {
          id: snapshotValue?.indicator_id,
          code,
          name: snapshotValue?.label,
        };
        const mapping = mappings.get(mappingKey("data_element", "indicator", indicator.id, indicator.code));
        if (!isMapped(mapping)) unmapped.push(indicator.code || indicator.name || "indicator");
        return {
          dataElement: isMapped(mapping) ? mapping.dhis2_uid : `UNMAPPED_${indicator.code || indicator.id}`,
          period: report.reporting_period,
          orgUnit: isMapped(orgMapping) ? orgMapping.dhis2_uid : `UNMAPPED_${report.org_unit?.code || report.org_unit_id}`,
          value: numberOrNull(snapshotValue?.value_numeric) ?? 0,
          categoryOptionCombo: "HllvX50cXC0",
          attributeOptionCombo: "HllvX50cXC0",
          comment: previewComment(report),
        };
      });

    const payload = {
      previewOnly: true,
      note: "Preview only - not sent to DHIS2.",
      dataSet: isMapped(dataSetMapping) ? dataSetMapping.dhis2_uid : "UNMAPPED_DATA_SET",
      period: report.reporting_period,
      orgUnit: isMapped(orgMapping) ? orgMapping.dhis2_uid : `UNMAPPED_${report.org_unit?.code || report.org_unit_id}`,
      completeDate: new Date().toISOString().slice(0, 10),
      dataValues,
      afriMedMeta: {
        source: "regional_health_surveillance_reporting_chain",
        workflow: "regional_health_surveillance",
        reportId: report.id,
        reportVersion: approvedVersion,
        approvedOnly: true,
        programCode: report.program?.code,
        orgUnitCode: report.org_unit?.code,
        unmapped: Array.from(new Set(unmapped)),
        noLiveDhis2Call: true,
        noPatientLevelData: true,
      },
    };

    const hasUnmapped = unmapped.length > 0;
    const { data, error } = await supabase
      .from("regional_health_export_previews")
      .insert({
        report_id: reportId,
        report_version: approvedVersion,
        export_type: "dhis2_data_value_set_preview",
        payload_json: payload,
        quality_status: hasUnmapped ? "quality_failed" : "preview_only",
        created_by: (await supabase.auth.getUser())?.data?.user?.id || null,
      })
      .select()
      .single();

    if (error) return { data: null, error };

    const transitionRes = await supabase.rpc("regional_health_record_export_preview", {
      target_report: reportId,
      target_preview: data.id,
    });
    if (transitionRes.error) return { data: null, error: transitionRes.error };

    return { data: { preview: data, payload }, error: null };
  },

  async getExportPreviews(reportId) {
    const query = supabase
      .from("regional_health_export_previews")
      .select("*")
      .order("created_at", { ascending: false });
    const { data, error } = reportId ? await query.eq("report_id", reportId) : await query.limit(50);
    return { data: data || [], error };
  },

  async listActorAssignments() {
    const { data, error } = await supabase
      .from("regional_health_surveillance_actor_assignments")
      .select("*, org_unit:regional_health_org_units(*)")
      .eq("status", "active")
      .order("starts_at", { ascending: true });
    return { data: data || [], error };
  },

  async submitReport(reportId, assignmentId) {
    return supabase.rpc("regional_health_submit_report", {
      target_report: reportId,
      target_assignment: assignmentId,
      target_source_channel: "facility_reporting",
    });
  },

  async transitionReport(reportId, assignmentId, action, reason = "") {
    if (action === "advance_to_district") {
      return supabase.rpc("regional_health_advance_to_district", {
        target_report: reportId,
        target_assignment: assignmentId,
        target_source_channel: "regional_health_surveillance",
      });
    }
    return supabase.rpc("regional_health_transition_report", {
      target_report: reportId,
      target_assignment: assignmentId,
      target_action: action,
      target_reason: safeText(reason) || null,
      target_source_channel: "regional_health_surveillance",
    });
  },
};

export default regionalHealthService;
